import type { PaginatedListResponse, SortDirection } from '@features/allocation';

/** `projeto.ind_situacao` do Axiis, como no `ProjetoHeaderList.php` do legado. */
export const PROJECT_STATUS_IDS = [0, 1, 2, 3, 4] as const;

export type ProjectStatus = (typeof PROJECT_STATUS_IDS)[number];

/** Unidades do Axiis (`system_unit`), na ordem do id. */
export const PROJECT_UNITS = [
  { id: 1, code: 'BR' },
  { id: 2, code: 'US' },
] as const;

export type ProjectManager = {
  /** `gestor.id` — o que o combo de edição seleciona. */
  id: number;
  registrationNumber: number | null;
  name: string;
};

/** Tipo de gestor (combo da edição); mapeado para `gestor_tipo_id` no back. */
export type ManagerKind = 'projectManager' | 'operationManager' | 'accountDirector';

/** Opção do lookup `GET /managers`: `id` é o `gestor.id`. */
export type ManagerOption = {
  id: number;
  registrationNumber: number | null;
  name: string;
};

/** Corpo do `PATCH /projects/management/:id` (admin): só os 5 campos liberados. */
export type ProjectUpdateBody = {
  projectManagerId: number | null;
  operationManagerId: number | null;
  accountDirectorId: number | null;
  isInternal: boolean;
  isPool: boolean;
};

/** Linha de `GET /projects/management`. */
export type ProjectRow = {
  id: number;
  axisCode: string;
  name: string;
  /** Número cru: o back repassa valor fora de `PROJECT_STATUS_IDS` como veio. */
  status: number;
  /** `YYYY-MM-DD`; nulos quando o Access não informou. */
  startDate: string | null;
  endDate: string | null;
  unit: { id: number; code: string };
  contract: { id: number; contractCode: number | null; name: string } | null;
  client: { id: number; name: string } | null;
  projectManager: ProjectManager | null;
  /** `gerente_carteira` no Axiis. */
  operationManager: ProjectManager | null;
  accountDirector: ProjectManager | null;
  isInternal: boolean;
  isPool: boolean;
};

export type ProjectSortField =
  | 'unit'
  | 'contract'
  | 'axisCode'
  | 'name'
  | 'status'
  | 'startDate'
  | 'endDate'
  | 'projectManager'
  | 'operationManager'
  | 'accountDirector'
  | 'internal'
  | 'pool';

/** Sem `sortBy`, a ordem do legado (`id`). */
export type ProjectFilters = {
  axisCode?: string;
  name?: string;
  clientSearch?: string;
  contractSearch?: string;
  unitId?: number;
  statusIds?: number[];
  projectManagerSearch?: string;
  operationManagerSearch?: string;
  accountDirectorSearch?: string;
  internal?: boolean;
  pool?: boolean;
  sortBy?: ProjectSortField;
  sortDirection?: SortDirection;
  page: number;
  /** Ausente = o padrão do back (20). */
  perPage?: number;
};

export type ProjectsResponse = PaginatedListResponse<ProjectRow>;

/** `page`: só a página que a tela mostra; `all`: todas, com os mesmos filtros. */
export type ProjectExportScope = 'all' | 'page';
